import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { StatusPage } from 'components';
import {
  BookValuesContext,
  BookLocationInitialValuesContext,
  MobileAppValuesContext,
  NetworkStatusValuesContext,
  UserContentValuesContext,
  UserSessionValuesContext,
} from 'contexts';
import {
  useCreateBookmarkMutation,
  useDeleteBookmarkMutation,
  useCreateEpubAnnotationMutation,
  useDeleteEpubAnnotationMutation,
  useUpdateEpubAnnotationMutation,
  useCreateCopiedTextMutation,
  useCreateFlashcardMutation,
  useUpdateFlashcardMutation,
  useDeleteFlashcardMutation,
  useCreateFlashcardDeckMutation,
  useCreateOrUpdateOfflineSessionMutation,
  useDeleteOfflineSessionMutation,
  useQueryForUserContent,
} from 'hooks';
import {
  UserContent,
  UserContentItem,
  UserContentBookmark,
  UserContentCopiedText,
  UserContentFlashcard,
  UserContentFlashcardDeck,
  UserContentHighlight,
  UserContentNote,
  UserContentOfflineSession,
} from 'types';
import { exportUserContent, importUserContent } from '../../utilities';
import { ALL_USER_CONTENT_REFETCH } from 'constants';

// The UserContentProvider takes care of fetching the user data
// and keeping track of any created content while offline by additionalUserContentItems
// which will sync with the backend once the user goes back online
export const UserContentProvider = ({ children }) => {
  const { cache } = useApolloClient();
  const { book } = useContext(BookValuesContext);
  const [userContent, setUserContent] = useState(null);
  // holds user content created offline before it is persisted to the backend
  const [additionalUserContentItems, setAdditionalUserContentItems] =
    useState(null);

  const userContentRef = useRef(null);

  const { initialBookLocation } = useContext(BookLocationInitialValuesContext);
  const { deviceUUID } = useContext(UserSessionValuesContext);
  const { isMobileApp, isMobileAppWithNewSync } = useContext(
    MobileAppValuesContext,
  );
  const { isOnline } = useContext(NetworkStatusValuesContext);

  // Track IndexedDB changes and when UUID->ID associations become available.
  const [lastLocalDatabaseUpdate, setLastLocalDatabaseUpdate] = useState(null);

  const { data, error, refetchUserContent, areAllQueriesCompleted } =
    useQueryForUserContent(book, initialBookLocation.documentIndex);
  const [relevantData, setRelevantData] = useState(data);

  const [createBookmarkMutation] = useCreateBookmarkMutation();
  const [deleteBookmarkMutation] = useDeleteBookmarkMutation();
  const [createAnnotationMutation] = useCreateEpubAnnotationMutation(
    initialBookLocation.documentIndex,
  );
  const [updateAnnotationMutation] = useUpdateEpubAnnotationMutation();
  const [deleteAnnotationMutation] = useDeleteEpubAnnotationMutation();
  const [createCopiedTextMutation] = useCreateCopiedTextMutation();
  const [createFlashcardMutation] = useCreateFlashcardMutation();
  const [updateFlashcardMutation] = useUpdateFlashcardMutation();
  const [deleteFlashcardMutation] = useDeleteFlashcardMutation();
  const [createFlashcardDeckMutation] = useCreateFlashcardDeckMutation();
  const [createOrUpdateOfflineSessionMutation] =
    useCreateOrUpdateOfflineSessionMutation();
  const [deleteOfflineSessionMutation] = useDeleteOfflineSessionMutation();

  const refetchContent = useCallback(
    async (contentToRefetch = ALL_USER_CONTENT_REFETCH) => {
      if (isOnline || isMobileApp) {
        // delete all cache from the query so we can get fresh data
        contentToRefetch.forEach((content) => {
          content.fieldName.forEach((field) =>
            cache.evict({ fieldName: field }),
          );
        });
        cache.gc();

        await refetchUserContent(contentToRefetch);
      }
    },
    [isMobileApp, isOnline, cache, refetchUserContent],
  );

  useEffect(function triggerSideEffectsOnIDBUpdate() {
    UserContentItem.onIndexedDBUpdate = () =>
      setLastLocalDatabaseUpdate(new Date().getTime());
  }, []);

  const [waitingPersistance, setWaitingPersistance] = useState(false);

  useEffect(
    function getOfflineUserContents() {
      if (waitingPersistance) {
        // wait for persisting to end before fetching offline data from store,
        // coz store might be clearing when persisiting
        return;
      }

      (async () => {
        const highlights = await UserContentHighlight.getLocalItems(book);
        const notes = await UserContentNote.getLocalItems(book);
        const bookmarks = await UserContentBookmark.getLocalItems(book);
        const copiedTexts = await UserContentCopiedText.getLocalItems(book);
        const flashcards = await UserContentFlashcard.getLocalItems(book);
        const flashcardDecks =
          await UserContentFlashcardDeck.getLocalItems(book);
        const offlineSessions =
          await UserContentOfflineSession.getLocalItems(book);

        setAdditionalUserContentItems([
          ...highlights,
          ...notes,
          ...bookmarks,
          ...copiedTexts,
          ...flashcards,
          ...flashcardDecks,
          ...offlineSessions,
        ]);
      })();
    },
    [book, lastLocalDatabaseUpdate, waitingPersistance],
  );

  useEffect(
    function persistOfflineUserContents() {
      if (!isOnline) {
        if (!isMobileApp) {
          // Web reader needs a real internet connection to continue.
          return;
        }
        if (isMobileAppWithNewSync) {
          // Newer versions of the React Native mobile app also need a real internet connection to continue.
          return;
        }
        // Older versions of the mobile app, both Xamarin and React Native, will intercept network requests.
        // Therefore, they do not need to be online to continue.
      }

      if (
        !additionalUserContentItems?.length || // nothing in store so nothing to persist
        !areAllQueriesCompleted // wait for fresh data to come from query before starting persisting
      ) {
        return;
      }

      setWaitingPersistance(true); // persisting has started.

      UserContentHighlight.persistLocalItems(book, {
        createMutation: createAnnotationMutation,
        updateMutation: updateAnnotationMutation,
        deleteMutation: deleteAnnotationMutation,
      });
      UserContentNote.persistLocalItems(book, {
        createMutation: createAnnotationMutation,
        updateMutation: updateAnnotationMutation,
        deleteMutation: deleteAnnotationMutation,
      });
      UserContentBookmark.persistLocalItems(book, {
        createMutation: createBookmarkMutation,
        deleteMutation: deleteBookmarkMutation,
      });
      UserContentCopiedText.persistLocalItems(book, {
        createMutation: createCopiedTextMutation,
      });
      UserContentOfflineSession.persistLocalItems(
        book,
        {
          updateMutation: createOrUpdateOfflineSessionMutation,
          deleteMutation: deleteOfflineSessionMutation,
        },
        refetchContent,
      );

      // Decks must be persisted before flashcards so that their database ids are available.
      UserContentFlashcardDeck.persistLocalItems(book, {
        createMutation: createFlashcardDeckMutation,
      }).then(() => {
        UserContentFlashcard.persistLocalItems(book, {
          createMutation: createFlashcardMutation,
          updateMutation: updateFlashcardMutation,
          deleteMutation: deleteFlashcardMutation,
        });
      });
    },
    [
      additionalUserContentItems,
      areAllQueriesCompleted,
      book,
      isMobileApp,
      isMobileAppWithNewSync,
      isOnline,
      createAnnotationMutation,
      updateAnnotationMutation,
      deleteAnnotationMutation,
      createBookmarkMutation,
      deleteBookmarkMutation,
      createCopiedTextMutation,
      createFlashcardMutation,
      updateFlashcardMutation,
      deleteFlashcardMutation,
      createFlashcardDeckMutation,
      createOrUpdateOfflineSessionMutation,
      deleteOfflineSessionMutation,
      refetchContent,
    ],
  );

  // Waits for all content being persisted to the backend to finish persisting.
  useEffect(
    function checkIfPersistingCompleted() {
      if (!(isOnline || isMobileApp)) {
        // not online nor mobileApp (always online for mobileApp))
        setWaitingPersistance(false);
        return;
      }

      for (let type of [
        UserContentHighlight,
        UserContentNote,
        UserContentBookmark,
        UserContentCopiedText,
        UserContentFlashcard,
        UserContentFlashcardDeck,
        UserContentOfflineSession,
      ]) {
        if (type.isPersistingLocalItems) {
          return;
        }
      }

      setWaitingPersistance(false); // persisting has completed.
    },
    [
      additionalUserContentItems,
      isMobileApp,
      isOnline,
      lastLocalDatabaseUpdate,
    ],
  );

  // Waiting for persistence to complete prevents a moment of "double highlighting" from rendering.
  useEffect(
    function updateData() {
      if (!data || error) {
        return;
      }

      if (waitingPersistance) {
        return;
      }

      setRelevantData(data);
    },
    [book, data, error, waitingPersistance],
  );

  useEffect(
    function createNewUserContent() {
      if (
        !relevantData || // no data
        error || // error when fetching data
        waitingPersistance || // wait for persisting to complete to prevent flashing
        !additionalUserContentItems // check local storage atleast once for offline data
      ) {
        return;
      }

      // a new version of the book is constructed when we go offline -> online
      // lets make sure the additionalUserContentItems have the chance to update their refs
      // so we can prevent flashing
      if (
        additionalUserContentItems?.[0] &&
        book !== additionalUserContentItems[0]?.book
      ) {
        return;
      }

      const userContent = new UserContent({
        data: relevantData,
        previousData: userContentRef.current,
        book,
        deviceUUID,
        additionalUserContentItems,
        refetch: refetchContent,
      });

      userContentRef.current = userContent;

      window.v7.userContent = userContent;
      window.v7.exportUserContent = () => exportUserContent();
      window.v7.importUserContent = (exportedObject) =>
        importUserContent(book, exportedObject);

      setUserContent(userContent);
    },
    [
      book,
      deviceUUID,
      error,
      relevantData,
      refetchContent,
      additionalUserContentItems,
      waitingPersistance,
    ],
  );

  const values = useMemo(() => userContent, [userContent]);

  if (error) {
    return (
      <StatusPage
        statusCode={error.networkError.statusCode}
        bookPk={book?.pk}
      />
    );
  }

  return (
    <UserContentValuesContext.Provider value={values}>
      {children}
    </UserContentValuesContext.Provider>
  );
};

export default UserContentProvider;
